import { useState, useEffect, useRef, useCallback } from "react"
import { useMsal } from "@azure/msal-react"
import { createLatestRequestGuard } from "@/lib/latest-request"
import { currentApplicableProgramYear } from "@/lib/program-year"
import { useNavigate } from "react-router-dom"
import {
  IconWorldWww,
  IconUpload,
  IconPlayerPlay,
  IconFileExport,
  IconLoader2,
  IconChevronLeft,
  IconChevronRight,
  IconChevronsLeft,
  IconChevronsRight,
  IconX,
  IconCheck,
  IconAlertTriangle,
  IconEye,
  IconClipboardCheck,
  IconExternalLink,
} from "@tabler/icons-react"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { ConfidenceBadge } from "@/components/confidence-badge"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select"
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table"
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/components/ui/tabs"
import { Separator } from "@/components/ui/separator"
import { Skeleton } from "@/components/ui/skeleton"
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet"
import { Textarea } from "@/components/ui/textarea"
import { Label } from "@/components/ui/label"
import { Alert, AlertDescription } from "@/components/ui/alert"
import { api, apiClient } from "@/lib/api"
import { onNotification } from "@/lib/signalr"
import { formatPersonName } from "@/lib/person-identity"
import { Checkbox } from "@/components/ui/checkbox"
import { useAppSelector } from "@/store"
import { canBulkUpdateAllStaffProgress, canBulkUpdateStaffProgress, canManagePseDistribution } from "@/app/pse-online/permissions"
import { pseBenefitResumePath } from "@/lib/pse-create-handoff"
import { buildDistributionSelection, distributionPreviewKey } from "@/app/pse-online/distributionUi"
import { ManualAssignment } from "@/app/pse-online/manual-assignment"

// ── Types ──

interface PseOnlineRecordSummary {
  id: string
  uploadId: string
  rowNumber: number
  pseApplicationId: number
  programYear: number
  applicationCreatedDate: string
  pseStatus: string
  pseStatusLabel?: string
  preProductionStatus: number | string
  staffProgressStatus: number | string
  staffProgressVersion: number
  staffProgressChangedAt: string | null
  staffProgressChangedBy: string | null
  applicationType: string | null
  accountHolderFirstName: string | null
  accountHolderLastName: string | null
  accountNumber: string | null
  serviceAddress: string | null
  errorMessage: string | null
  processedAt: string | null
  assignedStaffId: string | null
  assignedStaffDisplayName: string | null
  assignmentVersion: number
  designationStatus: number | null
  overallConfidence: number | null
  selfAttestationStatus: string | null
  lastUploadDate: string | null
}

interface PaginatedResponse<T> {
  items: T[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

interface PseOnlineUpload {
  id: string
  originalFileName: string
  processingStatus: number
  totalRecords: number
  processedRecords: number
  errorRecords: number
  uploadedAt: string
  uploadedByUserId: string
  processingCompletedAt: string | null
  errorMessage: string | null
}

interface PseUploadProgress {
  uploadId: string
  step: string
  processed: number
  skipped: number
  errors: number
  total: number
}

interface PseStatusOption {
  value: string
  label: string
}

interface PseDistributionCapability {
  canManageDistribution: boolean
  canViewStaffOptions: boolean
  maxSelection: number
}

interface PseDistributionStaffOption {
  id: string
  displayName: string
  userPrincipalName: string
}

interface PseDistributionPreview {
  previewId: string | null
  selectedCount: number
  assignableCount: number
  unassignableCount: number
  message: string
  expiresAt: string | null
  items: Array<{
    recordId: string
    eligible: boolean
    reason: string
    selfAttestationClassification: string | null
    assignedStaffId: string | null
    assignedTier: number | null
  }>
  staffCounts: Array<{ staffId: string; displayName: string; tier: number; count: number }>
}

const processingStatusLabels: Record<number, string> = {
  0: "Uploaded",
  1: "Parsing",
  2: "Waiting for matching",
  3: "Processing",
  4: "Completed",
  5: "Failed",
}

const staffProgressStatusLabels: Record<number, string> = {
  0: "Not Started",
  1: "In Progress",
  2: "Processed",
}

function staffProgressStatusValue(status: number | string): number {
  if (typeof status === "number") return status
  const numeric = Number(status)
  if (Number.isInteger(numeric) && numeric >= 0) return numeric
  return ({ notstarted: 0, inprogress: 1, processed: 2 } as Record<string, number>)[status.toLowerCase()] ?? -1
}

function StaffProgressBadge({ status }: { status: number | string }) {
  const value = staffProgressStatusValue(status)
  const label = staffProgressStatusLabels[value] ?? `Unknown (${status})`
  if (value === 1) return <Badge variant="secondary">{label}</Badge>
  if (value === 2) return <Badge className="bg-green-600 text-white">{label}</Badge>
  return <Badge variant="outline">{label}</Badge>
}

// ── Match Document & Review Queue Types ──

interface MatchDocumentResponse {
  id: string
  csvRecordId: string
  status: number
  matchDocument: string
  assignedStaffId?: string
  notes?: string
  createdBenefitId?: string
  createdAt: string
  updatedAt: string
}

interface MatchDocument {
  accountHolder: {
    csvData: { firstName: string; lastName: string; middleInitial: string; ssnLast4?: string; dateOfBirth?: string; accountNumber: string; applicantType?: string }
    matches: Array<{ personId: string; confidence: number; matchType: string; matchedFields: string[] }>
    decision: "use_existing" | "create_new" | null
    selectedPersonId: string | null
    newPersonData: { firstName: string; lastName: string; middleName?: string; dateOfBirth: string; gender: number; ssnLast4?: string } | null
  }
  coCustomer?: {
    csvData: { firstName: string; lastName: string; middleInitial?: string; ssnLast4?: string; dateOfBirth?: string; accountNumber?: string; applicantType?: string }
    matches: Array<{ personId: string; confidence: number; matchType: string; matchedFields: string[] }>
    decision: "use_existing" | "create_new" | null
    selectedPersonId: string | null
    newPersonData: { firstName: string; lastName: string; middleName?: string; dateOfBirth: string; gender: number; ssnLast4?: string } | null
  } | null
  householdMembers: Array<{ index: number; ageRange: string; decision: string; newPersonData: unknown }>
  previousApplications: Array<{ benefitId: string; fileNumber: number; programYear: number; applicantName?: string; address: string; confidence: number; matchSource: string; householdComposition?: unknown }>
  overallConfidence: number
}

interface ValidationResult {
  isValid: boolean
  warnings: Array<{ field: string; message: string; matchedPersonId?: string; confidence?: number }>
  errors: Array<{ field: string; message: string }>
}

interface CreateBenefitResult {
  success: boolean
  benefitId?: string
  reviewQueueItemId?: string
  message: string
}

interface ReviewQueueItemSummary {
  id: string
  matchDocumentId: string
  csvRecordId: string
  pseApplicationId: number
  accountHolderName: string
  reason: string
  status: number
  reviewedById?: string
  reviewedAt?: string
  createdAt: string
}

interface ReviewQueueItemDetail extends ReviewQueueItemSummary {
  canReject?: boolean
  matchDocument: string
  reviewNotes?: string
  creationCapability?: {
    mode: "V2" | "Compatibility" | "Rejected" | "AlreadyCreated" | string
    canCreate: boolean
    canApprove: boolean
    disabledReason?: string
    createdBenefitId?: string
  }
}

interface ReviewQueuePagedResult {
  items: ReviewQueueItemSummary[]
  totalCount: number
  page: number
  pageSize: number
  totalPages: number
}

const designationStatusLabels: Record<number, string> = {
  0: "Not Started",
  1: "In Progress",
  2: "Ready for Creation",
  3: "Needs Review",
  4: "Rejected",
  5: "Approved",
  6: "Completed",
}

// ── Helper Components ──

function DesignationStatusBadge({ status }: { status: number }) {
  const label = designationStatusLabels[status] ?? `Unknown (${status})`
  switch (status) {
    case 0: return <Badge variant="outline">{label}</Badge>
    case 1: return <Badge variant="secondary">{label}</Badge>
    case 2: return <Badge className="bg-blue-600 text-white">{label}</Badge>
    case 3: return <Badge className="bg-yellow-500 text-black">{label}</Badge>
    case 4: return <Badge variant="destructive">{label}</Badge>
    case 5: return <Badge className="bg-green-600 text-white">{label}</Badge>
    case 6: return <Badge variant="default">{label}</Badge>
    default: return <Badge variant="outline">{label}</Badge>
  }
}

function ReviewStatusBadge({ status }: { status: number }) {
  switch (status) {
    case 0: return <Badge variant="outline">Pending</Badge>
    case 1: return <Badge className="bg-green-600 text-white">Approved</Badge>
    case 2: return <Badge variant="destructive">Rejected</Badge>
    default: return <Badge variant="outline">Unknown</Badge>
  }
}

function formatMmDdYyyy(date: string | null | undefined): string {
  if (!date) return "—"
  const match = date.match(/^(\d{4})-(\d{2})-(\d{2})/)
  if (match) return `${match[2]}/${match[3]}/${match[1]}`
  const parsed = new Date(date)
  if (Number.isNaN(parsed.getTime())) return date
  const mm = String(parsed.getMonth() + 1).padStart(2, "0")
  const dd = String(parsed.getDate()).padStart(2, "0")
  return `${mm}/${dd}/${parsed.getFullYear()}`
}

function SortableColumnHeader({
  label,
  field,
  sortBy,
  sortDirection,
  onSort,
}: {
  label: string
  field: string
  sortBy: string
  sortDirection: string
  onSort: (field: string) => void
}) {
  const selected = sortBy === field
  return (
    <TableHead aria-sort={selected ? (sortDirection === "asc" ? "ascending" : "descending") : "none"}>
      <button
        type="button"
        className="inline-flex items-center gap-1 font-medium hover:underline"
        onClick={() => onSort(field)}
      >
        {label}
        <span className="text-xs text-muted-foreground" aria-hidden="true">
          {selected ? (sortDirection === "asc" ? "↑" : "↓") : "↕"}
        </span>
      </button>
    </TableHead>
  )
}

// ── Review Detail Sheet ──

function ReviewDetailSheet({
  itemId,
  open,
  onOpenChange,
  onAction,
}: {
  itemId: string | null
  open: boolean
  onOpenChange: (open: boolean) => void
  onAction?: () => void
}) {
  const navigate = useNavigate()
  const [detail, setDetail] = useState<ReviewQueueItemDetail | null>(null)
  const [parsedDoc, setParsedDoc] = useState<MatchDocument | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [notes, setNotes] = useState("")
  const [acting, setActing] = useState(false)
  const [actionMessage, setActionMessage] = useState<string | null>(null)

  useEffect(() => {
    if (!itemId || !open) return
    setLoading(true)
    setError(null)
    setDetail(null)
    setParsedDoc(null)
    setNotes("")
    setActionMessage(null)

    api
      .get<ReviewQueueItemDetail>(`/api/pse-online/review-queue/${itemId}`)
      .then((data) => {
        setDetail(data)
        try {
          setParsedDoc(JSON.parse(data.matchDocument) as MatchDocument)
        } catch {
          // invalid JSON
        }
      })
      .catch((err) => setError(err instanceof Error ? err.message : "Failed to load review item"))
      .finally(() => setLoading(false))
  }, [itemId, open])

  const handleAction = async (action: "approve" | "reject") => {
    if (!itemId) return
    setActing(true)
    setActionMessage(null)
    try {
      const resp = await apiClient(`/api/pse-online/review-queue/${itemId}/${action}`, {
        method: "POST",
        body: JSON.stringify({ notes: notes || null }),
      })
      if (!resp.ok) throw new Error(`${action} failed: ${resp.status}`)
      setActionMessage(`Successfully ${action === "approve" ? "approved" : "rejected"}`)
      onAction?.()
    } catch (err) {
      setActionMessage(err instanceof Error ? err.message : `${action} failed`)
    } finally {
      setActing(false)
    }
  }

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent className="w-full sm:max-w-xl overflow-y-auto overflow-x-hidden">
        <SheetHeader>
          <SheetTitle>Review Queue Item</SheetTitle>
          <SheetDescription>
            {detail ? `PSE App #${detail.pseApplicationId} — ${detail.accountHolderName}` : "Loading..."}
          </SheetDescription>
          {detail?.creationCapability && (
            <Alert variant={!detail.creationCapability.canApprove && detail.creationCapability.mode !== "AlreadyCreated" ? "destructive" : "default"}>
              <AlertDescription>
                <span className="font-medium">Server decision: {detail.creationCapability.mode}.</span>{" "}
                {detail.creationCapability.mode === "AlreadyCreated" && detail.creationCapability.createdBenefitId
                  ? <>
                      CMS benefit {detail.creationCapability.createdBenefitId} is already committed.
                      <Button type="button" variant="link" size="sm" className="h-auto px-1 py-0 align-baseline" onClick={() => navigate(pseBenefitResumePath(detail.creationCapability!.createdBenefitId!))}>
                        Open / Resume application <IconExternalLink className="ml-1 inline size-3" />
                      </Button>
                    </>
                  : detail.creationCapability.disabledReason ?? "Approval capability is determined by server permissions and source eligibility."}
              </AlertDescription>
            </Alert>
          )}
        </SheetHeader>

        {loading && (
          <div className="space-y-3 p-4">
            <Skeleton className="h-4 w-3/4" />
            <Skeleton className="h-32 w-full" />
          </div>
        )}

        {error && <div className="p-4 text-sm text-destructive">{error}</div>}

        {detail && (
          <div className="flex flex-col gap-4 p-4">
            <Card>
              <CardContent>
                <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-4 gap-y-1.5 text-sm">
                  <dt className="text-muted-foreground">Status</dt>
                  <dd><ReviewStatusBadge status={detail.status} /></dd>
                  <dt className="text-muted-foreground">Reasons</dt>
                  <dd className="flex flex-col items-start gap-1 min-w-0">
                    {(() => {
                      try {
                        return (JSON.parse(detail.reason) as string[]).map((r, i) => (
                          <Badge key={i} variant="secondary" className="text-xs whitespace-normal break-words text-left max-w-full">{r}</Badge>
                        ))
                      } catch {
                        return <Badge variant="secondary" className="text-xs whitespace-normal break-words text-left max-w-full">{detail.reason}</Badge>
                      }
                    })()}
                  </dd>
                  <dt className="text-muted-foreground">Created</dt>
                  <dd>{new Date(detail.createdAt).toLocaleString()}</dd>
                  {detail.reviewedAt && (
                    <>
                      <dt className="text-muted-foreground">Reviewed</dt>
                      <dd>{new Date(detail.reviewedAt).toLocaleString()}</dd>
                    </>
                  )}
                  {detail.reviewNotes && (
                    <>
                      <dt className="text-muted-foreground">Review Notes</dt>
                      <dd>{detail.reviewNotes}</dd>
                    </>
                  )}
                </dl>
              </CardContent>
            </Card>

            {parsedDoc && (
              <>
                <Card>
                  <CardHeader className="pb-2">
                    <CardTitle className="text-sm">Account Holder</CardTitle>
                    <CardDescription className="text-xs">
                      {formatPersonName(parsedDoc.accountHolder.csvData)}
                    </CardDescription>
                  </CardHeader>
                  <CardContent className="text-xs space-y-1">
                    <div>Decision: <Badge variant="outline">{parsedDoc.accountHolder.decision ?? "None"}</Badge></div>
                    {parsedDoc.accountHolder.selectedPersonId && (
                      <div>Selected Person: <span className="font-mono">{parsedDoc.accountHolder.selectedPersonId}</span></div>
                    )}
                    {parsedDoc.accountHolder.newPersonData && (
                      <div>
                        New Person: {formatPersonName(parsedDoc.accountHolder.newPersonData)}
                        {parsedDoc.accountHolder.newPersonData.dateOfBirth && ` — DOB: ${parsedDoc.accountHolder.newPersonData.dateOfBirth}`}
                      </div>
                    )}
                    <div>Matches: {parsedDoc.accountHolder.matches.length}</div>
                  </CardContent>
                </Card>

                {parsedDoc.coCustomer && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Co-Customer</CardTitle>
                      <CardDescription className="text-xs">
                        {formatPersonName(parsedDoc.coCustomer.csvData)}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-xs space-y-1">
                      <div>Decision: <Badge variant="outline">{parsedDoc.coCustomer.decision ?? "None"}</Badge></div>
                      {parsedDoc.coCustomer.selectedPersonId && (
                        <div>Selected Person: <span className="font-mono">{parsedDoc.coCustomer.selectedPersonId}</span></div>
                      )}
                      <div>Matches: {parsedDoc.coCustomer.matches.length}</div>
                    </CardContent>
                  </Card>
                )}

                {parsedDoc.previousApplications.length > 0 && (
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Previous Applications ({parsedDoc.previousApplications.length})</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {parsedDoc.previousApplications.map((app, i) => (
                        <a
                          key={i}
                          href={`/benefit-applications/${app.benefitId}`}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center justify-between gap-2 text-xs border rounded p-2 hover:bg-accent hover:border-ring transition-colors"
                        >
                          <div className="min-w-0">
                            <span className="font-mono font-medium underline underline-offset-2 inline-flex items-center gap-1">
                              File #{app.fileNumber}
                              <IconExternalLink className="size-3" />
                            </span>
                            <span className="text-muted-foreground ml-2">({app.programYear})</span>
                            <div className="text-muted-foreground truncate">{app.address}</div>
                          </div>
                          <ConfidenceBadge confidence={app.confidence} />
                        </a>
                      ))}
                    </CardContent>
                  </Card>
                )}

                <div className="flex items-center gap-2 text-sm">
                  <span>Overall Confidence:</span>
                  <ConfidenceBadge confidence={parsedDoc.overallConfidence} />
                </div>
              </>
            )}

            {detail.status === 0 && (
              <>
                <Separator />
                <div className="space-y-2">
                  <Label className="text-sm">Notes (optional)</Label>
                  <Textarea
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    placeholder="Add review notes..."
                    rows={3}
                  />
                </div>
                <div className="flex gap-2">
                  <Button onClick={() => handleAction("approve")} disabled={acting || detail?.creationCapability?.canApprove !== true || detail?.creationCapability?.mode === "AlreadyCreated"} className="bg-green-600 hover:bg-green-700">
                    {acting && <IconLoader2 className="size-4 animate-spin mr-1" />}
                    <IconCheck className="size-4 mr-1" />
                    Approve
                  </Button>
                  <Button onClick={() => handleAction("reject")} disabled={acting || detail?.canReject !== true} variant="destructive">
                    {acting && <IconLoader2 className="size-4 animate-spin mr-1" />}
                    <IconX className="size-4 mr-1" />
                    Reject
                  </Button>
                </div>
                {actionMessage && <p className="text-sm text-muted-foreground">{actionMessage}</p>}
              </>
            )}
          </div>
        )}
      </SheetContent>
    </Sheet>
  )
}

// ── Main Page ──

export default function PseOnlinePage() {
  const navigate = useNavigate()
  const permissions = useAppSelector((state) => state.permissions.permissions)
  const { accounts } = useMsal()
  const currentUserId = accounts[0]?.localAccountId?.toLowerCase() ?? ""
  const canBulkUpdate = canBulkUpdateStaffProgress(permissions)
  const canBulkUpdateAll = canBulkUpdateAllStaffProgress(permissions)
  const canBulkUpdateRecord = useCallback((record: PseOnlineRecordSummary) =>
    canBulkUpdateAll || (record.assignedStaffId?.toLowerCase() === currentUserId && currentUserId.length > 0),
    [canBulkUpdateAll, currentUserId])
  const [distributionCapability, setDistributionCapability] = useState<PseDistributionCapability | null>(null)
  const [distributionStaff, setDistributionStaff] = useState<PseDistributionStaffOption[]>([])
  const canManageDistribution = distributionCapability?.canManageDistribution === true && canManagePseDistribution(permissions)
  const canSelectRecords = canBulkUpdate || canManageDistribution

  // Records state
  const [records, setRecords] = useState<PseOnlineRecordSummary[]>([])
  const [recordsLoading, setRecordsLoading] = useState(false)
  const [recordsError, setRecordsError] = useState<string | null>(null)
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const pageSize = 50

  // Record filters
  const [pseStatusFilter, setPseStatusFilter] = useState<string>("all")
  const [pseStatusOptions, setPseStatusOptions] = useState<PseStatusOption[]>([])
  const [appTypeFilter, setAppTypeFilter] = useState<string>("supported")
  const [programYear, setProgramYear] = useState<string>(() => String(currentApplicableProgramYear()))
  const [dateFrom, setDateFrom] = useState<string>("")
  const [dateTo, setDateTo] = useState<string>("")
  const [applicationIdFilter, setApplicationIdFilter] = useState<string>("")
  const [accountHolderFilter, setAccountHolderFilter] = useState<string>("")
  const [accountNumberFilter, setAccountNumberFilter] = useState<string>("")
  const [assignedStaffFilter, setAssignedStaffFilter] = useState<string>("all")
  const [staffProgressFilter, setStaffProgressFilter] = useState<string>("all")
  const [sortBy, setSortBy] = useState<string>("AppDate")
  const [sortDirection, setSortDirection] = useState<string>("desc")

  const [selectedRecordIds, setSelectedRecordIds] = useState<Set<string>>(new Set())
  const [bulkStatus, setBulkStatus] = useState<string>("1")
  const [bulkUpdating, setBulkUpdating] = useState(false)
  const [bulkMessage, setBulkMessage] = useState<string | null>(null)
  const [selectedDistributionStaff, setSelectedDistributionStaff] = useState<Record<string, number>>({})
  const [distributionPreview, setDistributionPreview] = useState<PseDistributionPreview | null>(null)
  const [distributionLoading, setDistributionLoading] = useState(false)
  const [distributionMessage, setDistributionMessage] = useState<string | null>(null)
  const currentDistributionKey = useRef("")
  currentDistributionKey.current = distributionPreviewKey(
    buildDistributionSelection(records, selectedRecordIds),
    Object.entries(selectedDistributionStaff).map(([staffId, tier]) => ({ staffId, tier })),
  )

  // Uploads state
  const [uploads, setUploads] = useState<PseOnlineUpload[]>([])
  const [uploadsLoading, setUploadsLoading] = useState(false)
  const [uploadsError, setUploadsError] = useState<string | null>(null)
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)
  const [processingUploadId, setProcessingUploadId] = useState<string | null>(null)
  const fileInputRef = useRef<HTMLInputElement>(null)

  // Real-time upload progress from SignalR
  const [uploadProgress, setUploadProgress] = useState<Record<string, PseUploadProgress>>({})

  useEffect(() => {
    const unsubscribe = onNotification((type, payload) => {
      if (type === "pse-upload-progress") {
        const progress = payload as PseUploadProgress
        if (progress.step === "Completed" || progress.step === "Failed") {
          setUploadProgress((prev) => {
            const next = { ...prev }
            delete next[progress.uploadId]
            return next
          })
          fetchUploads()
        } else {
          setUploadProgress((prev) => ({ ...prev, [progress.uploadId]: progress }))
        }
      }
    })
    return unsubscribe
  }, [])

  // Active tab (for lazy-loading tab data)
  const [activeTab, setActiveTab] = useState("records")

  // Review Queue state
  const [reviewItems, setReviewItems] = useState<ReviewQueueItemSummary[]>([])
  const [reviewLoading, setReviewLoading] = useState(false)
  const [reviewError, setReviewError] = useState<string | null>(null)
  const [reviewTotalCount, setReviewTotalCount] = useState(0)
  const [selectedReviewId, setSelectedReviewId] = useState<string | null>(null)
  const [reviewSheetOpen, setReviewSheetOpen] = useState(false)

  // SDH File Creation state
  const [sdhRecords, setSdhRecords] = useState<Array<PseOnlineRecordSummary & { matchDocStatus?: number; createdBenefitId?: string }>>([])
  const [sdhLoading, setSdhLoading] = useState(false)
  const [sdhError, setSdhError] = useState<string | null>(null)

  // ── Fetch records ──

  const recordsRequests = useRef(createLatestRequestGuard())
  useEffect(() => () => recordsRequests.current.invalidate(), [])

  const fetchRecords = useCallback(async () => {
    const isCurrent = recordsRequests.current.start()
    setRecordsLoading(true)
    setRecordsError(null)
    // Retained rows are display-only until the refresh finishes. Do not carry selections
    // or an in-flight distribution preview across a new page/filter/ownership snapshot.
    setSelectedRecordIds(new Set())
    setDistributionPreview(null)
    currentDistributionKey.current = ""
    try {
      const params = new URLSearchParams()
      params.set("Page", String(page))
      params.set("PageSize", String(pageSize))
      if (staffProgressFilter !== "all") params.set("StaffProgressStatus", staffProgressFilter)
      if (pseStatusFilter !== "all") params.set("PseStatus", pseStatusFilter)
      if (appTypeFilter === "all") params.set("IncludeUnsupportedTypes", "true")
      else if (appTypeFilter !== "supported") params.set("ApplicationType", appTypeFilter)
      if (programYear) params.set("ProgramYear", programYear)
      if (dateFrom) params.set("ApplicationDateFrom", dateFrom)
      if (dateTo) params.set("ApplicationDateTo", dateTo)
      if (applicationIdFilter) params.set("ApplicationId", applicationIdFilter)
      if (accountHolderFilter.trim()) params.set("AccountHolder", accountHolderFilter.trim())
      if (accountNumberFilter.trim()) params.set("AccountNumber", accountNumberFilter.trim())
      if (assignedStaffFilter !== "all") params.set("AssignedStaffId", assignedStaffFilter)
      params.set("SortBy", sortBy)
      params.set("SortDirection", sortDirection)

      const data = await api.get<PaginatedResponse<PseOnlineRecordSummary>>(
        `/api/pse-online/records?${params.toString()}`
      )
      if (!isCurrent()) return
      setRecords(data.items)
      setPage(data.page)
      setTotalPages(data.totalPages)
      setTotalCount(data.totalCount)
      // The summary includes the two list badges. Full match documents belong to the
      // detail workflow; waiting for up to 50 of them used to hide already-loaded rows.
    } catch (err) {
      if (isCurrent()) setRecordsError(err instanceof Error ? err.message : "Failed to fetch records")
    } finally {
      if (isCurrent()) setRecordsLoading(false)
    }
  }, [page, staffProgressFilter, pseStatusFilter, appTypeFilter, programYear, dateFrom, dateTo, applicationIdFilter, accountHolderFilter, accountNumberFilter, assignedStaffFilter, sortBy, sortDirection])

  useEffect(() => {
    fetchRecords()
  }, [fetchRecords])

  useEffect(() => {
    api.get<PseStatusOption[]>("/api/pse-online/status-options")
      .then(setPseStatusOptions)
      .catch(() => setPseStatusOptions([]))
    api.get<PseDistributionCapability>("/api/pse-online/distribution/capability")
      .then((capability) => {
        setDistributionCapability(capability)
        if (capability.canViewStaffOptions) {
          return api.get<PseDistributionStaffOption[]>("/api/pse-online/distribution/staff-options")
        }
        return []
      })
      .then(setDistributionStaff)
      .catch(() => {
        setDistributionCapability(null)
        setDistributionStaff([])
      })
  }, [])

  // Reset to page 1 when filters change
  const updateFilter = (setter: (v: string) => void) => (value: string) => {
    setter(value)
    setPage(1)
  }

  const toggleRecordSelection = (recordId: string, checked: boolean) => {
    setSelectedRecordIds((previous) => {
      const next = new Set(previous)
      if (checked) next.add(recordId)
      else next.delete(recordId)
      return next
    })
  }

  const toggleAllVisibleRecords = (checked: boolean) => {
    setSelectedRecordIds((previous) => {
      const next = new Set(previous)
      records.forEach((record) => {
        const selectable = canManageDistribution || canBulkUpdateRecord(record)
        if (selectable) {
          if (checked) next.add(record.id)
          else next.delete(record.id)
        }
      })
      return next
    })
  }

  const handleBulkStaffProgress = async () => {
    const selected = records.filter((record) => selectedRecordIds.has(record.id))
    if (selected.length === 0) return
    setBulkUpdating(true)
    setBulkMessage(null)
    try {
      await api.patch("/api/pse-online/records/staff-progress", {
        status: Number(bulkStatus),
        updates: selected.map((record) => ({
          id: record.id,
          expectedVersion: record.staffProgressVersion,
          expectedAssignmentVersion: record.assignmentVersion,
        })),
      })
      setSelectedRecordIds(new Set())
      setBulkMessage(`Updated ${selected.length} record${selected.length === 1 ? "" : "s"}.`)
      await fetchRecords()
    } catch (error) {
      setBulkMessage(error instanceof Error ? error.message : "Bulk status update failed. Reload and try again.")
    } finally {
      setBulkUpdating(false)
    }
  }

  const toggleDistributionStaff = (staffId: string, checked: boolean) => {
    setSelectedDistributionStaff((previous) => {
      const next = { ...previous }
      if (checked) next[staffId] = 0
      else delete next[staffId]
      return next
    })
    setDistributionPreview(null)
  }

  const setDistributionTier = (staffId: string, tier: string) => {
    setSelectedDistributionStaff((previous) => ({ ...previous, [staffId]: Number(tier) }))
    setDistributionPreview(null)
  }

  const previewDistribution = async () => {
    const selected = buildDistributionSelection(records, selectedRecordIds)
    const staff = Object.entries(selectedDistributionStaff).map(([staffId, tier]) => ({ staffId, tier }))
    if (selected.length === 0 || staff.length === 0) {
      setDistributionMessage("Select at least one visible record and one target staff member.")
      return
    }
    const requestKey = currentDistributionKey.current
    setDistributionLoading(true)
    setDistributionMessage(null)
    try {
      const preview = await api.post<PseDistributionPreview>("/api/pse-online/distribution/preview", {
        records: selected,
        staff,
      })
      if (requestKey !== currentDistributionKey.current) {
        setDistributionPreview(null)
        setDistributionMessage("The selection or staff tiers changed while previewing. Preview again.")
        return
      }
      setDistributionPreview(preview)
      setDistributionMessage(preview.message)
    } catch (error) {
      setDistributionMessage(error instanceof Error ? error.message : "Preview failed. Reload and try again.")
      setDistributionPreview(null)
    } finally {
      setDistributionLoading(false)
    }
  }

  const applyDistribution = async () => {
    if (!distributionPreview?.previewId) return
    setDistributionLoading(true)
    setDistributionMessage(null)
    try {
      const result = await api.post<{ message: string }>("/api/pse-online/distribution/apply", {
        previewId: distributionPreview.previewId,
      })
      setDistributionMessage(result.message)
      setDistributionPreview(null)
      setSelectedRecordIds(new Set())
      await fetchRecords()
    } catch (error) {
      setDistributionMessage(error instanceof Error ? error.message : "Distribution was not applied. Reload and preview again.")
    } finally {
      setDistributionLoading(false)
    }
  }

  const handleSort = (field: string) => {
    if (sortBy === field) {
      setSortDirection((direction) => direction === "asc" ? "desc" : "asc")
    } else {
      setSortBy(field)
      setSortDirection("asc")
    }
    setPage(1)
  }

  // ── Uploads ──

  const fetchUploads = async () => {
    setUploadsLoading(true)
    setUploadsError(null)
    try {
      const data = await api.get<PseOnlineUpload[]>("/api/pse-online/uploads")
      setUploads(data)
    } catch (error) {
      setUploadsError(error instanceof Error ? error.message : "Failed to fetch uploads")
    } finally {
      setUploadsLoading(false)
    }
  }

  const handleUpload = async (file: File) => {
    setUploading(true)
    setUploadError(null)
    try {
      const formData = new FormData()
      formData.append("file", file)
      await api.upload<unknown>("/api/pse-online/upload", formData)
      await fetchUploads()
    } catch (error) {
      setUploadError(error instanceof Error ? error.message : "Upload failed")
    } finally {
      setUploading(false)
      if (fileInputRef.current) fileInputRef.current.value = ""
    }
  }

  const triggerProcessing = async (uploadId: string) => {
    setProcessingUploadId(uploadId)
    try {
      const resp = await apiClient(`/api/pse-online/uploads/${uploadId}/process`, { method: "POST" })
      if (!resp.ok) throw new Error(`Processing trigger failed: ${resp.status}`)
      await fetchUploads()
    } catch {
      // silently ignore — user can retry
    } finally {
      setProcessingUploadId(null)
    }
  }

  const onFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    if (!file.name.endsWith(".csv")) {
      setUploadError("Only .csv files are accepted")
      e.target.value = ""
      return
    }
    handleUpload(file)
  }

  useEffect(() => {
    fetchUploads()
  }, [])

  // Graceful degradation for live progress: while any upload is mid-flight (Uploaded/Parsing/Awaiting
  // Review/Processing) poll the uploads list so progress keeps advancing even if the SignalR websocket
  // is unavailable. SignalR, when connected, still provides faster event-driven updates.
  const fetchUploadsRef = useRef(fetchUploads)
  fetchUploadsRef.current = fetchUploads
  const hasActiveUpload = uploads.some((u) => u.processingStatus <= 3)
  const recordsPartiallyAvailable = uploads.some((u) => u.processingStatus <= 3)
  useEffect(() => {
    if (!hasActiveUpload) return
    const id = window.setInterval(() => { void fetchUploadsRef.current() }, 4000)
    return () => window.clearInterval(id)
  }, [hasActiveUpload])

  const openDetail = (id: string) => {
    navigate(`/pse-online/${id}`)
  }

  // ── Review Queue ──

  const fetchReviewQueue = useCallback(async () => {
    setReviewLoading(true)
    setReviewError(null)
    try {
      const data = await api.get<ReviewQueuePagedResult>(
        "/api/pse-online/review-queue?Status=0&Page=1&PageSize=50"
      )
      setReviewItems(data.items)
      setReviewTotalCount(data.totalCount)
    } catch (err) {
      setReviewError(err instanceof Error ? err.message : "Failed to fetch review queue")
    } finally {
      setReviewLoading(false)
    }
  }, [])

  // ── SDH File Creation ──

  const fetchSdhRecords = useCallback(async () => {
    setSdhLoading(true)
    setSdhError(null)
    try {
      // This dedicated workflow tab still uses the persisted SDH stage. The Applications
      // list above deliberately has no pre-production filter or retained filter state.
      const params = new URLSearchParams({
        PreProductionStatus: "1",
        PageSize: "100",
        Page: "1",
      })
      if (programYear) params.set("ProgramYear", programYear)

      const data = await api.get<PaginatedResponse<PseOnlineRecordSummary>>(
        `/api/pse-online/records?${params.toString()}`
      )
      const enriched = await Promise.all(
        data.items.map(async (r) => {
          try {
            const doc = await api.get<MatchDocumentResponse>(`/api/pse-online/records/${r.id}/match-document`)
            return { ...r, matchDocStatus: doc.status, createdBenefitId: doc.createdBenefitId, assignedStaffId: doc.assignedStaffId ?? null }
          } catch {
            return { ...r }
          }
        })
      )
      setSdhRecords(enriched)
    } catch (err) {
      setSdhError(err instanceof Error ? err.message : "Failed to fetch SDH records")
    } finally {
      setSdhLoading(false)
    }
  }, [programYear])

  const handleTabChange = (tab: string) => {
    setActiveTab(tab)
    if (tab === "records") fetchRecords()
    if (tab === "uploads") fetchUploads()
    if (tab === "review-queue") fetchReviewQueue()
    if (tab === "sdh") fetchSdhRecords()
  }

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6 px-4 lg:px-6">
      <div>
        <div className="flex items-center gap-2">
          <IconWorldWww className="size-6" />
          <h1 className="text-2xl font-bold">PSE Online</h1>
        </div>
        <p className="mt-1 text-muted-foreground">
          Import PSE Online applications from ReportExtractor CSV exports. Screen, match, and create SDH benefit files.
        </p>
        {(permissions.includes("pse-online:manage") || permissions.includes("admin:manage-access")) && (
          <Button className="mt-3" variant="outline" onClick={() => navigate("/pse-online/snapshot-import")}>
            Import PSE snapshot file
          </Button>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={handleTabChange}>
        <TabsList>
          <TabsTrigger value="records">Applications</TabsTrigger>
          <TabsTrigger value="uploads">CSV Uploads</TabsTrigger>
          <TabsTrigger value="review-queue">Review Queue</TabsTrigger>
          <TabsTrigger value="sdh">SDH File Creation</TabsTrigger>
        </TabsList>

        {/* ===== Applications List ===== */}
        <TabsContent value="records" className="flex flex-col gap-4">
          {recordsPartiallyAvailable && (
            <Alert>
              <IconAlertTriangle className="size-4" />
              <AlertDescription>
                This application list is incomplete while a PSE import is being processed. New rows and matches may appear as processing advances; source PSE statuses are shown unchanged.
              </AlertDescription>
            </Alert>
          )}
          <div className="flex flex-wrap items-center gap-3">
            <Select value={staffProgressFilter} onValueChange={updateFilter(setStaffProgressFilter)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Staff Progress" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Staff Progress</SelectItem>
                <SelectItem value="0">Not Started</SelectItem>
                <SelectItem value="1">In Progress</SelectItem>
                <SelectItem value="2">Processed</SelectItem>
              </SelectContent>
            </Select>

            <Select value={assignedStaffFilter} onValueChange={updateFilter(setAssignedStaffFilter)}>
              <SelectTrigger className="w-[190px]">
                <SelectValue placeholder="Assigned staff" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Assigned Staff</SelectItem>
                <SelectItem value="unassigned">Unassigned</SelectItem>
                {distributionStaff.map((staff) => (
                  <SelectItem key={staff.id} value={staff.id}>{staff.displayName}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={pseStatusFilter} onValueChange={updateFilter(setPseStatusFilter)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="PSE Status" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All PSE Statuses</SelectItem>
                {pseStatusOptions.map((option) => (
                  <SelectItem key={option.value} value={option.value}>{option.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>

            <Select value={appTypeFilter} onValueChange={updateFilter(setAppTypeFilter)}>
              <SelectTrigger className="w-[180px]">
                <SelectValue placeholder="Application Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="supported">Hide BDR</SelectItem>
                <SelectItem value="all">All Types (incl. BDR)</SelectItem>
                <SelectItem value="PSE_HELP">PSE Help only</SelectItem>
                <SelectItem value="BDR">BDR only</SelectItem>
              </SelectContent>
            </Select>

            <Input
              type="number"
              placeholder="Year"
              className="w-[100px]"
              value={programYear}
              onChange={(e) => { setProgramYear(e.target.value); setPage(1) }}
            />

            <Input
              type="date"
              className="w-[160px]"
              value={dateFrom}
              onChange={(e) => { setDateFrom(e.target.value); setPage(1) }}
              placeholder="From"
            />
            <Input
              type="date"
              className="w-[160px]"
              value={dateTo}
              onChange={(e) => { setDateTo(e.target.value); setPage(1) }}
              placeholder="To"
            />

            <Input
              type="number"
              className="w-[150px]"
              value={applicationIdFilter}
              onChange={(e) => { setApplicationIdFilter(e.target.value); setPage(1) }}
              placeholder="Application ID"
            />
            <Input
              className="w-[180px]"
              value={accountHolderFilter}
              onChange={(e) => { setAccountHolderFilter(e.target.value); setPage(1) }}
              placeholder="Account Holder"
            />
            <Input
              className="w-[160px]"
              value={accountNumberFilter}
              onChange={(e) => { setAccountNumberFilter(e.target.value); setPage(1) }}
              placeholder="Account Number"
            />

            <span className="text-sm text-muted-foreground">
              {totalCount} record{totalCount !== 1 ? "s" : ""}
            </span>
          </div>

          {canManageDistribution && (
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-base">Supervisor distribution</CardTitle>
                <CardDescription>
                  Select visible rows only (maximum {distributionCapability?.maxSelection ?? 200}). Availability and tier apply to this request; they do not change staff setup.
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <ManualAssignment records={records.filter(record => selectedRecordIds.has(record.id))} staff={distributionStaff} onAssigned={async () => {
                  setDistributionPreview(null)
                  setSelectedRecordIds(new Set())
                  await fetchRecords()
                }} />
                <p className="text-sm font-medium">Distribute untouched new applications by tier</p>
                <div className="flex flex-wrap gap-2">
                  {distributionStaff.map((staff) => {
                    const selected = Object.prototype.hasOwnProperty.call(selectedDistributionStaff, staff.id)
                    return (
                      <div key={staff.id} className="flex items-center gap-2 rounded border px-2 py-1.5">
                        <Checkbox
                          checked={selected}
                          aria-label={`Include ${staff.displayName} in distribution`}
                          onCheckedChange={(checked) => toggleDistributionStaff(staff.id, checked === true)}
                        />
                        <span className="text-sm">{staff.displayName}</span>
                        {selected && (
                          <Select value={String(selectedDistributionStaff[staff.id])} onValueChange={(value) => setDistributionTier(staff.id, value)}>
                            <SelectTrigger className="h-8 w-[145px]" aria-label={`${staff.displayName} distribution tier`}>
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="0">All applications</SelectItem>
                              <SelectItem value="1">Eligible only</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      </div>
                    )
                  })}
                </div>
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-sm font-medium">{selectedRecordIds.size} visible row{selectedRecordIds.size === 1 ? "" : "s"} selected</span>
                  <Button size="sm" onClick={previewDistribution} disabled={distributionLoading || selectedRecordIds.size === 0}>
                    {distributionLoading && <IconLoader2 className="mr-1 size-4 animate-spin" />}
                    Preview distribution
                  </Button>
                  {distributionMessage && <span className="text-sm text-muted-foreground">{distributionMessage}</span>}
                </div>
                {distributionPreview && (
                  <div className="space-y-2 rounded border bg-muted/20 p-3 text-sm">
                    <div className="font-medium">Exact preview: {distributionPreview.assignableCount} assignable, {distributionPreview.unassignableCount} unchanged</div>
                    <div className="flex flex-wrap gap-2">
                      {distributionPreview.staffCounts.map((count) => (
                        <Badge key={`${count.staffId}-${count.tier}`} variant="secondary">
                          {count.displayName}: {count.count} ({count.tier === 0 ? "all" : "eligible-only"})
                        </Badge>
                      ))}
                    </div>
                    <ul className="max-h-48 overflow-y-auto space-y-1 text-xs">
                      {distributionPreview.items.map((item) => (
                        <li key={item.recordId} className={item.assignedStaffId ? "text-foreground" : "text-muted-foreground"}>
                          <span className="font-mono">{item.recordId}</span>: {item.reason}
                        </li>
                      ))}
                    </ul>
                    <Button size="sm" onClick={applyDistribution} disabled={distributionLoading || !distributionPreview.previewId}>
                      {distributionLoading && <IconLoader2 className="mr-1 size-4 animate-spin" />}
                      Confirm exact preview
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {canBulkUpdate && (
            <div className="flex flex-wrap items-center gap-2 rounded-md border bg-muted/30 p-3">
              <span className="text-sm font-medium">{selectedRecordIds.size} selected</span>
              <Select value={bulkStatus} onValueChange={setBulkStatus}>
                <SelectTrigger className="w-[160px]" aria-label="Bulk staff progress status">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="0">Not Started</SelectItem>
                  <SelectItem value="1">In Progress</SelectItem>
                  <SelectItem value="2">Processed</SelectItem>
                </SelectContent>
              </Select>
              <Button
                size="sm"
                onClick={handleBulkStaffProgress}
                disabled={bulkUpdating || selectedRecordIds.size === 0 || records.some((record) => selectedRecordIds.has(record.id) && !canBulkUpdateRecord(record))}
              >
                {bulkUpdating && <IconLoader2 className="mr-1 size-4 animate-spin" />}
                Apply status
              </Button>
              {!canBulkUpdateAll && <span className="text-sm text-muted-foreground">Bulk progress applies only to records assigned to you.</span>}
              {bulkMessage && <span className="text-sm text-muted-foreground">{bulkMessage}</span>}
            </div>
          )}

          {recordsLoading && <p role="status" className="text-sm text-muted-foreground">
            {records.length > 0 ? "Updating applications…" : "Loading applications…"}
          </p>}
          <Card>
            <CardContent className="p-0">
              {recordsLoading && records.length === 0 ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 8 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : recordsError ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-destructive">{recordsError}</p>
                  <Button variant="outline" size="sm" onClick={fetchRecords}>Retry</Button>
                </div>
              ) : (
                <Table aria-busy={recordsLoading} inert={recordsLoading || undefined} className={recordsLoading ? "opacity-60" : undefined}>
                  <TableHeader>
                    <TableRow>
                      {canSelectRecords && (
                        <TableHead>
                          <Checkbox
                            aria-label="Select all visible records"
                            checked={records.some((record) => canManageDistribution || canBulkUpdateRecord(record)) && records.filter((record) => canManageDistribution || canBulkUpdateRecord(record)).every((record) => selectedRecordIds.has(record.id))}
                            onCheckedChange={(checked) => toggleAllVisibleRecords(checked === true)}
                          />
                        </TableHead>
                      )}
                      <SortableColumnHeader label="Application ID" field="ApplicationId" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Year" field="ProgramYear" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Account Holder" field="AccountHolder" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Account #" field="AccountNumber" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Service Address" field="ServiceAddress" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="PSE Status" field="PseStatus" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Confidence" field="Confidence" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Designation" field="Designation" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <TableHead>Staff Progress</TableHead>
                      <SortableColumnHeader label="Intake" field="Intake" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Self Attestation Status" field="SelfAttestationStatus" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="Last Upload Date" field="LastUploadDate" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                      <SortableColumnHeader label="App Date" field="AppDate" sortBy={sortBy} sortDirection={sortDirection} onSort={handleSort} />
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {records.map((record) => {
                      const confidence = record.overallConfidence
                      return (
                        <TableRow
                          key={record.id}
                          className="cursor-pointer"
                          onClick={() => openDetail(record.id)}
                        >
                          {canSelectRecords && (
                            <TableCell onClick={(event) => event.stopPropagation()}>
                              <Checkbox
                                aria-label={`Select application ${record.pseApplicationId}`}
                                checked={selectedRecordIds.has(record.id)}
                                disabled={!canManageDistribution && !canBulkUpdateRecord(record)}
                                onCheckedChange={(checked) => toggleRecordSelection(record.id, checked === true)}
                              />
                            </TableCell>
                          )}
                          <TableCell className="font-mono text-xs">{record.pseApplicationId}</TableCell>
                          <TableCell>{record.programYear}</TableCell>
                          <TableCell className="font-medium">
                            {formatPersonName({ lastName: record.accountHolderLastName, firstName: record.accountHolderFirstName })}
                            {record.applicationType === "BDR" && (
                              <Badge variant="outline" className="ml-2 text-amber-600 border-amber-300">BDR</Badge>
                            )}
                          </TableCell>
                          <TableCell className="font-mono text-xs">{record.accountNumber ?? "—"}</TableCell>
                          <TableCell className="text-xs">{record.serviceAddress ?? "—"}</TableCell>
                          <TableCell>
                            <Badge variant="outline" title={record.pseStatus}>
                              {record.pseStatusLabel ?? record.pseStatus}
                            </Badge>
                          </TableCell>
                          <TableCell>
                            {confidence != null ? <ConfidenceBadge confidence={confidence} /> : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell>
                            {record.designationStatus != null ? <DesignationStatusBadge status={record.designationStatus} /> : <span className="text-muted-foreground text-xs">—</span>}
                          </TableCell>
                          <TableCell><StaffProgressBadge status={record.staffProgressStatus} /></TableCell>
                          <TableCell className="text-xs" title={record.assignedStaffId ?? undefined}>
                            {record.assignedStaffDisplayName ?? record.assignedStaffId ?? "—"}
                          </TableCell>
                          <TableCell className="text-xs">{record.selfAttestationStatus ?? "—"}</TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {formatMmDdYyyy(record.lastUploadDate)}
                          </TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {formatMmDdYyyy(record.applicationCreatedDate)}
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    {records.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={canSelectRecords ? 14 : 13} className="text-center text-muted-foreground py-8">
                          No records found for the current filters.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          {/* Pagination */}
          <div className="flex items-center justify-between">
            <span className="text-sm text-muted-foreground">
              Page {Math.min(page, Math.max(totalPages, 1))} of {Math.max(totalPages, 1)}
            </span>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || totalCount === 0}
                onClick={() => setPage(1)}
                aria-label="First page"
              >
                <IconChevronsLeft className="size-4" />
                First
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1 || totalCount === 0}
                onClick={() => setPage((p) => Math.max(1, p - 1))}
              >
                <IconChevronLeft className="size-4" />
                Previous
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || totalCount === 0}
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              >
                Next
                <IconChevronRight className="size-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages || totalCount === 0}
                onClick={() => setPage(totalPages)}
                aria-label="Last page"
              >
                Last
                <IconChevronsRight className="size-4" />
              </Button>
            </div>
          </div>
        </TabsContent>

        {/* ===== CSV Uploads ===== */}
        <TabsContent value="uploads" className="flex flex-col gap-4">
          <div className="flex flex-col gap-3">
            <div className="flex items-center gap-3">
              <input
                ref={fileInputRef}
                type="file"
                accept=".csv"
                onChange={onFileChange}
                className="hidden"
                id="csv-upload"
              />
              <Button
                onClick={() => fileInputRef.current?.click()}
                disabled={uploading}
              >
                {uploading ? (
                  <IconLoader2 className="size-4 animate-spin" />
                ) : (
                  <IconUpload className="size-4" />
                )}
                {uploading ? "Uploading..." : "Upload CSV"}
              </Button>
              <span className="text-sm text-muted-foreground">
                ReportExtractor CSV export (max 100MB)
              </span>
            </div>
            {uploadError && (
              <p className="text-sm text-destructive">{uploadError}</p>
            )}
          </div>

          <Card>
            <CardContent className="p-0">
              {uploadsLoading ? (
                <div className="flex items-center justify-center py-8 gap-2 text-muted-foreground">
                  <IconLoader2 className="size-4 animate-spin" />
                  Loading uploads...
                </div>
              ) : uploadsError ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-destructive">{uploadsError}</p>
                  <Button variant="outline" size="sm" onClick={fetchUploads}>Retry</Button>
                </div>
              ) : (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Filename</TableHead>
                    <TableHead>Records</TableHead>
                    <TableHead>Errors</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Uploaded By</TableHead>
                    <TableHead>Uploaded</TableHead>
                    <TableHead className="w-[60px]"></TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {uploads.map((upload) => {
                    const sr = uploadProgress[upload.id]
                    // Live SignalR progress if present, otherwise derive from the polled upload counts
                    // (graceful degradation when the websocket is unavailable).
                    const isParsing = sr ? sr.step === "Parsing" : upload.processingStatus === 1
                    const isProcessing = sr ? sr.step === "Processing" : upload.processingStatus === 3
                    const showLive = sr != null || isParsing || isProcessing
                    const done = sr ? sr.processed + sr.skipped : upload.processedRecords
                    const total = sr ? sr.total : upload.totalRecords
                    const progErrors = sr ? sr.errors : upload.errorRecords
                    const determinate = isProcessing && total > 0
                    const pct = determinate ? Math.round((done / total) * 100) : 0
                    return (
                    <TableRow key={upload.id}>
                      <TableCell className="font-mono text-xs">{upload.originalFileName}</TableCell>
                      <TableCell>{upload.totalRecords.toLocaleString()}</TableCell>
                      <TableCell>
                        {upload.errorRecords > 0 ? (
                          <Badge variant="destructive">{upload.errorRecords}</Badge>
                        ) : (
                          <span className="text-muted-foreground">0</span>
                        )}
                      </TableCell>
                      <TableCell>
                        {showLive ? (
                          <div className="flex flex-col gap-1 min-w-[140px]">
                            <div className="flex items-center gap-1.5">
                              <IconLoader2 className="size-3 animate-spin text-muted-foreground" />
                              {determinate ? (
                                <>
                                  <span className="text-xs font-medium">{done.toLocaleString()}/{total.toLocaleString()}</span>
                                  <span className="text-xs text-muted-foreground">({pct}%)</span>
                                </>
                              ) : (
                                <span className="text-xs font-medium">
                                  {isParsing ? `Parsing… ${done.toLocaleString()}` : "Processing…"}
                                </span>
                              )}
                            </div>
                            <div className="h-1.5 w-full rounded-full bg-muted overflow-hidden">
                              {determinate ? (
                                <div
                                  className="h-full rounded-full bg-primary transition-all duration-300"
                                  style={{ width: `${pct}%` }}
                                />
                              ) : (
                                <div className="h-full w-1/3 rounded-full bg-primary animate-pulse" />
                              )}
                            </div>
                            {progErrors > 0 && (
                              <span className="text-xs text-destructive">{progErrors} errors</span>
                            )}
                          </div>
                        ) : (
                          <Badge variant={upload.processingStatus === 4 ? "default" : upload.processingStatus === 5 ? "destructive" : "secondary"}>
                            {processingStatusLabels[upload.processingStatus] ?? `Unknown (${upload.processingStatus})`}
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell className="text-xs">{upload.uploadedByUserId}</TableCell>
                      <TableCell className="text-muted-foreground text-xs">
                        {new Date(upload.uploadedAt).toLocaleString()}
                      </TableCell>
                      <TableCell>
                        <Button
                          variant="ghost"
                          size="icon"
                          title="Trigger processing"
                          disabled={processingUploadId === upload.id}
                          onClick={() => triggerProcessing(upload.id)}
                        >
                          {processingUploadId === upload.id ? (
                            <IconLoader2 className="size-4 animate-spin" />
                          ) : (
                            <IconPlayerPlay className="size-4" />
                          )}
                        </Button>
                      </TableCell>
                    </TableRow>
                    )
                  })}
                  {uploads.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                        No uploads yet. Upload a ReportExtractor CSV to get started.
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ===== Review Queue ===== */}
        <TabsContent value="review-queue" className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <IconClipboardCheck className="size-5" />
                Review Queue
              </h2>
              <p className="text-sm text-muted-foreground">
                {reviewTotalCount} item{reviewTotalCount !== 1 ? "s" : ""} pending review.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchReviewQueue} disabled={reviewLoading}>
              {reviewLoading && <IconLoader2 className="size-4 animate-spin mr-1" />}
              Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {reviewLoading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : reviewError ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-destructive">{reviewError}</p>
                  <Button variant="outline" size="sm" onClick={fetchReviewQueue}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PSE App ID</TableHead>
                      <TableHead>Account Holder</TableHead>
                      <TableHead>Reasons</TableHead>
                      <TableHead>Status</TableHead>
                      <TableHead>Created</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {reviewItems.map((item) => {
                      let reasons: string[] = []
                      try { reasons = JSON.parse(item.reason) as string[] } catch { reasons = [item.reason] }
                      return (
                        <TableRow key={item.id}>
                          <TableCell className="font-mono text-xs">{item.pseApplicationId}</TableCell>
                          <TableCell className="font-medium">{item.accountHolderName}</TableCell>
                          <TableCell>
                            <div className="flex flex-wrap gap-1">
                              {reasons.map((r, i) => (
                                <Badge key={i} variant="secondary" className="text-xs">{r}</Badge>
                              ))}
                            </div>
                          </TableCell>
                          <TableCell><ReviewStatusBadge status={item.status} /></TableCell>
                          <TableCell className="text-muted-foreground text-xs">
                            {formatMmDdYyyy(item.createdAt)}
                          </TableCell>
                          <TableCell>
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => { setSelectedReviewId(item.id); setReviewSheetOpen(true) }}
                            >
                              <IconEye className="size-4 mr-1" />
                              View
                            </Button>
                          </TableCell>
                        </TableRow>
                      )
                    })}
                    {reviewItems.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={6} className="text-center text-muted-foreground py-8">
                          No items in the review queue.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>

          <ReviewDetailSheet
            itemId={selectedReviewId}
            open={reviewSheetOpen}
            onOpenChange={setReviewSheetOpen}
            onAction={fetchReviewQueue}
          />
        </TabsContent>

        {/* ===== SDH File Creation ===== */}
        <TabsContent value="sdh" className="flex flex-col gap-4">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-semibold flex items-center gap-2">
                <IconFileExport className="size-5" />
                SDH File Creation
              </h2>
              <p className="text-sm text-muted-foreground">
                Records with completed benefit applications.
              </p>
            </div>
            <Button variant="outline" size="sm" onClick={fetchSdhRecords} disabled={sdhLoading}>
              {sdhLoading && <IconLoader2 className="size-4 animate-spin mr-1" />}
              Refresh
            </Button>
          </div>

          <Card>
            <CardContent className="p-0">
              {sdhLoading ? (
                <div className="p-4 space-y-2">
                  {Array.from({ length: 5 }).map((_, i) => (
                    <Skeleton key={i} className="h-8 w-full" />
                  ))}
                </div>
              ) : sdhError ? (
                <div className="flex flex-col items-center justify-center py-8 gap-2">
                  <p className="text-sm text-destructive">{sdhError}</p>
                  <Button variant="outline" size="sm" onClick={fetchSdhRecords}>Retry</Button>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>PSE App ID</TableHead>
                      <TableHead>Account Holder</TableHead>
                      <TableHead>Created Benefit ID</TableHead>
                      <TableHead>Processed Date</TableHead>
                      <TableHead>Staff</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {sdhRecords.map((record) => (
                      <TableRow key={record.id}>
                        <TableCell className="font-mono text-xs">{record.pseApplicationId}</TableCell>
                        <TableCell className="font-medium">
                          {formatPersonName({ lastName: record.accountHolderLastName, firstName: record.accountHolderFirstName })}
                        </TableCell>
                        <TableCell>
                          {record.createdBenefitId ? (
                            <span className="font-mono text-xs text-primary">{record.createdBenefitId}</span>
                          ) : (
                            <span className="text-muted-foreground">—</span>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground text-xs">
                          {record.processedAt ? new Date(record.processedAt).toLocaleString() : "—"}
                        </TableCell>
                        <TableCell className="text-xs" title={record.assignedStaffId ?? undefined}>
                          {record.assignedStaffDisplayName ?? record.assignedStaffId ?? "—"}
                        </TableCell>
                      </TableRow>
                    ))}
                    {sdhRecords.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No completed records found.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  )
}

